import { useState } from 'react';
import { Loader2 } from 'lucide-react';

import CtaLink from '@/components/CtaLink';
import { useLanguage } from '@/components/LanguageContext';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { getGbusinessConnectUrl } from '@/api/gbusinessService';
import CapturaPermisosGoogle from '@/components/gbusiness/CapturaPermisosGoogle';
import { reintentarConOtraCuenta, useGoogleConnectGuide } from '@/lib/googleConnectGuide';

const IDS = {
  ctaConectar: 'google.guia.conectar',
  ctaReintentar: 'google.guia.reintentar',
  ctaOtraCuenta: 'google.guia.otra-cuenta',
};

const PREFIJOS = {
  antes: 'gbusiness.connectGuide.antes',
  faltaCasilla: 'gbusiness.connectGuide.faltaCasilla',
  sinFicha: 'gbusiness.connectGuide.sinFicha',
};

/**
 * El modal antes de ir a Google (y al volver sin la casilla o sin ficha). Ver
 * `lib/googleConnectGuide.js`. Se monta UNA vez, en `App.jsx`.
 *
 * Sin botón de «Cancelar»: se cierra con la X del `DialogContent`.
 */
export default function GoogleConnectGuideDialog() {
  const { t } = useLanguage();
  const open = useGoogleConnectGuide((s) => s.open);
  const variant = useGoogleConnectGuide((s) => s.variant);
  const ctaId = useGoogleConnectGuide((s) => s.ctaId);
  const hintEmail = useGoogleConnectGuide((s) => s.hintEmail);
  const emptyEmail = useGoogleConnectGuide((s) => s.emptyEmail);
  const close = useGoogleConnectGuide((s) => s.close);
  // Mientras se borra la cuenta vacía, antes de navegar a Google.
  const [saliendo, setSaliendo] = useState(false);

  const faltaCasilla = variant === 'faltaCasilla';
  const sinFicha = variant === 'sinFicha';
  const prefijo = PREFIJOS[variant] || PREFIJOS.antes;

  // El botón del modal cuenta SU click (`google.guia.conectar`, meta `nav`),
  // pero la URL lleva el id del botón de ORIGEN: la conexión es de quien abrió
  // el modal, igual que el alta es del muro y no del `PricingModal` (§124).
  // En «falta una casilla» y «sin ficha» no hay origen —se abren solas al
  // volver del callback—, así que ese botón es el origen y lleva su propio id.
  //
  // Escritos en una tabla literal y no en un ternario para que la guarda de
  // `ctaRegistry.test.js` los vea (solo reconoce `ctaX: '…'`, `cta="…"`…).
  let botonCta = IDS.ctaConectar;
  if (faltaCasilla) botonCta = IDS.ctaReintentar;
  if (sinFicha) botonCta = IDS.ctaOtraCuenta;
  const oauthCta = faltaCasilla ? IDS.ctaReintentar : ctaId;

  const irAGoogle = () => {
    if (sinFicha) {
      setSaliendo(true);
      reintentarConOtraCuenta(emptyEmail, IDS.ctaOtraCuenta);
      return;
    }
    window.location.href = getGbusinessConnectUrl(oauthCta || undefined, hintEmail);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t(`${prefijo}.titulo`)}</DialogTitle>
          <DialogDescription>
            {sinFicha
              ? t(`${prefijo}.texto`, { email: emptyEmail || '' })
              : t(`${prefijo}.texto`)}
          </DialogDescription>
        </DialogHeader>
        {/* Los pasos, para que quede claro que la casilla se marca DESPUÉS y
            en Google. En «falta una casilla» sobran: el texto ya lo dice. */}
        {variant === 'antes' && (
          <ol className="list-decimal space-y-1 pl-5 text-sm text-gray-700">
            <li>{t('gbusiness.connectGuide.antes.paso1')}</li>
            <li>{t('gbusiness.connectGuide.antes.paso2')}</li>
          </ol>
        )}
        {/* «Sin ficha» no es un problema de casilla: la captura despistaría. */}
        {!sinFicha && <CapturaPermisosGoogle destacada />}
        <CtaLink
          as={Button}
          cta={botonCta}
          type="button"
          onClick={irAGoogle}
          disabled={saliendo}
          className="w-full"
        >
          {saliendo && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {t(`${prefijo}.boton`)}
        </CtaLink>
      </DialogContent>
    </Dialog>
  );
}
